/**
 * The words a hand swap and a refused gather show the player (AD-844; docs/design/guest-demo.md
 * §5.7). The SERVER swaps a gathering tool in for a gather and the weapon back in for a fight
 * (`backend/src/gateway/autoSwap.ts`); the client only says so — a short toast when the mainhand
 * changes — and turns the wire's refusal tokens into plain words. Pure, so the rules are testable.
 *
 * No number appears in any line (AD-321): a rank or tool requirement is named, never quantified.
 */

import type { InventorySnapshotPayload, ItemWire } from '@great-cloud/shared/client';
import { reasonWords } from '../ui/playerText.js';

/** The item in the character's mainhand, if any (`locationRef` names the character when given). */
export function mainhandOf(
  inv: Pick<InventorySnapshotPayload, 'items'> | null | undefined,
  characterRef?: string,
): ItemWire | undefined {
  return inv?.items.find(
    (i) =>
      i.location === 'equipped' &&
      i.slot === 'mainhand' &&
      (characterRef === undefined || i.locationRef === characterRef),
  );
}

/**
 * The toast for a mainhand change between two inventory pushes, or null when nothing changed or
 * this is the first push (the join's inventory is not a swap). Covers the server's auto-swap and
 * a hand equip alike: either way the player is told what is now in hand.
 */
export function handSwapToast(
  prev: Pick<InventorySnapshotPayload, 'items'> | null | undefined,
  next: Pick<InventorySnapshotPayload, 'items'>,
  characterRef?: string,
): string | null {
  if (!prev) return null;
  const before = mainhandOf(prev, characterRef);
  const after = mainhandOf(next, characterRef);
  if (!after || after.id === before?.id) return null;
  return `In hand: ${after.name}`;
}

/** The tool a gathering skill uses, as the player calls it. */
export const TOOL_NOUN: Readonly<Record<string, string>> = {
  herbalism: 'sickle',
  mining: 'pickaxe',
  forestry: 'axe',
  salvaging: 'pry bar',
  hunting: 'skinning knife',
};

/**
 * The words for a refused gather. `skill` is the node resource's gathering skill (from the
 * content pack), so a missing tool is named; unknown reasons keep their token.
 */
export function gatherRefusalText(reason: string | undefined, skill?: string): string {
  const tool = (skill && TOOL_NOUN[skill]) || 'gathering tool';
  switch (reason) {
    case 'toolRequired':
      // The server already tried the bag (AD-844): reaching this means none there will do.
      return `This needs a ${tool}, and none in your bag will do. Tools are crafted (see Crafting).`;
    case 'tierLocked':
      return `Your ${skill ?? 'gathering'} skill is not high enough for this yet`;
    case 'depleted':
      return 'Nothing left here — it grows back in a while';
    case 'outOfRange':
      return 'Too far away — walk closer';
    case 'tooSoon':
      return 'One at a time';
    case 'interrupted':
      return 'Gather interrupted';
    default:
      return reasonWords(reason);
  }
}

/** The words for a refused craft's common reasons; null for the rest (the caller's fallback). */
export function craftRefusalText(reason: string | undefined): string | null {
  switch (reason) {
    case 'noStation':
      return 'stand at the right crafting station first';
    case 'insufficientMaterials':
      return 'missing materials for this recipe';
    case 'insufficientShards':
      return 'not enough Shards for the station fee';
    default:
      return null;
  }
}
